import { META_PIXEL_ID } from '../config/siteConfig'

let initialized = false

// Loads the Meta Pixel base code and fires the first PageView. Entirely
// inert (does nothing at all) until META_PIXEL_ID is set in
// src/config/siteConfig.js - safe to ship before the real ID is available.
export const initMetaPixel = () => {
  if (initialized || !META_PIXEL_ID) return
  initialized = true

  /* eslint-disable */
  !function(f,b,e,v,n,t,s){if(f.fbq)return;n=f.fbq=function(){n.callMethod?
  n.callMethod.apply(n,arguments):n.queue.push(arguments)};if(!f._fbq)f._fbq=n;
  n.push=n;n.loaded=!0;n.version='2.0';n.queue=[];t=b.createElement(e);t.async=!0;
  t.src=v;s=b.getElementsByTagName(e)[0];s.parentNode.insertBefore(t,s)}(window,
  document,'script','https://connect.facebook.net/en_US/fbevents.js');
  /* eslint-enable */

  // MUST come before init. Meta's "autoConfig" feature scrapes the page's
  // title, meta description, meta keywords and every JSON-LD block, then
  // appends all of it to the query string of EVERY event it sends. Meta
  // sends events as a GET request and silently discards any whose URL grows
  // too long - no console error, no failed request, nothing in Events
  // Manager.
  //
  // Product pages carry the most metadata on this site (Product,
  // BreadcrumbList and FAQPage JSON-LD, plus a long product description),
  // and they crossed that limit. The symptom was baffling from the outside:
  // ViewContent never appeared in Events Manager even though fbq was
  // demonstrably being called and fbevents.js was loaded, while the exact
  // same call worked fine on /shop. Verified directly - with autoConfig on,
  // a ViewContent call produced no network request at all; with it off, the
  // identical call fired immediately.
  //
  // The trade-off is that this also turns off browser-side Automatic
  // Advanced Matching. That is an acceptable price here: the Conversions
  // API (worker/src/metaCapi.js) already sends hashed phone, name, city and
  // country server-side for Purchase - the event delivery optimization
  // actually runs on - and events that never arrive are worth far less than
  // events with slightly weaker matching.
  window.fbq('set', 'autoConfig', false, META_PIXEL_ID)

  window.fbq('init', META_PIXEL_ID)
  window.fbq('track', 'PageView')
}

// Call on every client-side route change after the first (the first
// PageView is already sent by initMetaPixel above).
export const trackPixelPageView = () => {
  if (META_PIXEL_ID && window.fbq) {
    window.fbq('track', 'PageView')
  }
}

// Every event below except PageView routes through this. React fires a
// child component's effects before its parent's, so a component that
// mounts straight onto a fresh page load (e.g. someone landing directly on
// a product page from an ad or a shared link) can run its tracking effect
// before App.jsx's own effect has called initMetaPixel() and defined
// window.fbq - the plain `window.fbq &&` guard these used to have would
// then silently skip the event. Calling initMetaPixel() here first
// (idempotent, safe to call any number of times) guarantees fbq exists
// before it's used, no matter which component's effect runs first.
const ensurePixelReady = () => {
  initMetaPixel()
  return META_PIXEL_ID && window.fbq
}

// Purchase is reported to Meta twice - once from here, and once from the
// Worker via the Conversions API (worker/src/metaCapi.js), because a large
// share of browser events never arrive (ad blockers, iOS/Safari tracking
// prevention, tabs closed early). Both copies carry the same id, which is
// how Meta collapses them into one conversion. Skip it and every order
// counts twice, roughly halving apparent cost-per-purchase and doubling
// reported ROAS.
export const newPixelEventId = () =>
  window.crypto?.randomUUID
    ? window.crypto.randomUUID()
    : `${Date.now()}-${Math.random().toString(16).slice(2)}`

// _fbp (dropped by the pixel on a visitor's first page) and _fbc (derived
// from the fbclid query param when someone arrives by clicking an ad) are
// the strongest signals Meta has for tying a server-side event back to the
// ad that caused it. They're browser cookies, so the Worker can't read them
// - Checkout posts them alongside the order instead.
export const getPixelCookies = () => {
  const read = (name) => {
    const match = document.cookie.split('; ').find((c) => c.startsWith(`${name}=`))
    // Split on the first '=' only: _fbc values contain '=' padding when the
    // fbclid is base64-ish, and slicing at the first separator keeps them intact.
    return match ? match.slice(name.length + 1) : null
  }
  return { fbp: read('_fbp'), fbc: read('_fbc') }
}

// Meta's standard Purchase event - fired once when an order is placed.
// This is what Meta Ads actually needs to optimize delivery toward people
// likely to buy, rather than just people likely to click. `eventId` pairs
// this with the Worker's server-side copy; see newPixelEventId above.
export const trackPixelPurchase = (order, eventId) => {
  if (ensurePixelReady()) {
    window.fbq(
      'track',
      'Purchase',
      {
        value: order.total,
        currency: 'PKR',
        content_ids: order.items.map((item) => item.id),
        content_type: 'product',
        num_items: order.items.reduce((sum, item) => sum + item.quantity, 0)
      },
      eventId ? { eventID: eventId } : undefined
    )
  }
}

// Fired when a product detail page loads. Purchase-only tracking gives
// Meta's ad delivery almost nothing to learn from - these three funnel
// events (this one, AddToCart, InitiateCheckout below) are what let it
// build real lookalike/retargeting audiences instead of optimizing blind.
export const trackPixelViewContent = (product) => {
  if (ensurePixelReady()) {
    window.fbq('track', 'ViewContent', {
      value: product.price,
      currency: 'PKR',
      content_ids: [product.id],
      content_name: product.name,
      content_type: 'product'
    })
  }
}

export const trackPixelAddToCart = (product, quantity, effectivePrice) => {
  if (ensurePixelReady()) {
    window.fbq('track', 'AddToCart', {
      value: effectivePrice * quantity,
      currency: 'PKR',
      content_ids: [product.id],
      content_name: product.name,
      content_type: 'product'
    })
  }
}

export const trackPixelInitiateCheckout = (cart, cartTotal) => {
  if (ensurePixelReady()) {
    window.fbq('track', 'InitiateCheckout', {
      value: cartTotal,
      currency: 'PKR',
      content_ids: cart.map((item) => item.id),
      content_type: 'product',
      num_items: cart.reduce((sum, item) => sum + item.quantity, 0)
    })
  }
}

// Fired when someone clicks a WhatsApp or phone link - the CLICK stage of
// those two funnels, which is the only stage a website can actually
// observe. Whatever happens next (a WhatsApp conversation, a phone call,
// and whether either becomes an order) happens entirely off this site, so
// there is no event for that - see the note in src/utils/attribution.js.
// `Contact` is Meta's own standard event name for exactly this kind of
// "reached out to the business" signal, which lets Ads Manager build
// lookalike/retargeting audiences from it the same way it does for
// AddToCart, rather than this being invisible to Meta entirely.
export const trackPixelContact = (method) => {
  if (ensurePixelReady()) {
    window.fbq('track', 'Contact', { content_category: method })
  }
}
